/**
 * Shared campaign journey definition (client mirror of the server's
 * journey.service). Conditional steps are inserted based on the campaign:
 *   - grant_access + confirm_access: digital campaigns (requires_shipping === false)
 *   - confirm_address + shipping: physical campaigns (requires_shipping !== false)
 *   - contract: custom-contract campaigns (contract_type === 'custom')
 *
 * Completion is derived from acceptance milestone columns so the UI stays
 * consistent with the server even before /journey is fetched.
 */

import { collaborationHasPostContent } from '../../components/deliverables/deliverablePostUrls';
import {
  getCommissionFeeModel,
  getFinalAcceptedFeeModels,
  hasOutstandingCampaignCommission,
  isPaymentStepComplete,
} from './collaborationCompensationUtils';
import {
  LIVESTREAM_REVIEW_STATUS,
  getLivestreamReviewStatus,
  isLivestreamAwaitingBrandReviewAfterResponse,
  needsLivestreamClarificationResponse,
} from './livestreamReview';
import {
  getContentLiveChipLabel,
  getContentLiveDeliverableContext,
  getContentLiveLabel,
  getContentLiveStepContext,
  getContentLiveSummary,
} from './contentLiveStepCopy';
import { campaignHasLivestreamDeliverables } from '../shared/campaignContentDeliverables';
import {
  formatPaymentAmountCents,
  getCollaborationPayoutAmounts,
} from './collaborationPayoutDisplay';

export const STEP_KEYS = {
  ACCEPTED: 'accepted',
  GRANT_ACCESS: 'grant_access',
  CONFIRM_ACCESS: 'confirm_access',
  CONFIRM_ADDRESS: 'confirm_address',
  SHIPPING: 'shipping',
  CONTRACT: 'contract',
  CONTENT_LIVE: 'content_live',
  PAID: 'paid',
  REVIEW: 'review',
};

export const JOURNEY_STATUSES = {
  NOT_STARTED: 'not_started',
  NEEDS_YOUR_ACTION: 'needs_your_action',
  WAITING_ON_BRAND: 'waiting_on_brand',
  WAITING_ON_CREATOR: 'waiting_on_creator',
  IN_REVIEW: 'in_review',
  CHANGES_REQUESTED: 'changes_requested',
  BLOCKED: 'blocked',
  SCHEDULED: 'scheduled',
  LIVE_SUBMITTED: 'live_submitted',
  PAYMENT_PENDING: 'payment_pending',
  PAID: 'paid',
  COMPLETE: 'complete',
  CANCELED: 'canceled',
};

const STATUS_LABELS = {
  [JOURNEY_STATUSES.NOT_STARTED]: 'Not started',
  [JOURNEY_STATUSES.NEEDS_YOUR_ACTION]: 'Needs your action',
  [JOURNEY_STATUSES.WAITING_ON_BRAND]: 'Waiting on brand',
  [JOURNEY_STATUSES.WAITING_ON_CREATOR]: 'Waiting on creator',
  [JOURNEY_STATUSES.IN_REVIEW]: 'In review',
  [JOURNEY_STATUSES.CHANGES_REQUESTED]: 'Changes requested',
  [JOURNEY_STATUSES.BLOCKED]: 'Blocked',
  [JOURNEY_STATUSES.SCHEDULED]: 'Scheduled',
  [JOURNEY_STATUSES.LIVE_SUBMITTED]: 'Submitted',
  [JOURNEY_STATUSES.PAYMENT_PENDING]: 'Payment pending',
  [JOURNEY_STATUSES.PAID]: 'Paid',
  [JOURNEY_STATUSES.COMPLETE]: 'Complete',
  [JOURNEY_STATUSES.CANCELED]: 'Canceled',
};

const STEP_BADGES = {
  [STEP_KEYS.CONTRACT]: 'Custom only',
};

const BRAND_LABELS = {
  [STEP_KEYS.ACCEPTED]: 'Accepted',
  [STEP_KEYS.GRANT_ACCESS]: 'Product access',
  [STEP_KEYS.CONFIRM_ACCESS]: 'Access confirmation',
  [STEP_KEYS.CONFIRM_ADDRESS]: 'Shipping address',
  [STEP_KEYS.SHIPPING]: 'Product shipment',
  [STEP_KEYS.CONTRACT]: 'Contract',
  [STEP_KEYS.CONTENT_LIVE]: 'Content submission',
  [STEP_KEYS.PAID]: 'Payment',
  [STEP_KEYS.REVIEW]: 'Collaboration review',
};

const INFLUENCER_LABELS = {
  ...BRAND_LABELS,
  [STEP_KEYS.GRANT_ACCESS]: 'Product access',
  [STEP_KEYS.CONFIRM_ACCESS]: 'Confirm access',
  [STEP_KEYS.CONFIRM_ADDRESS]: 'Shipping address',
  [STEP_KEYS.SHIPPING]: 'Product shipment',
};

// One-line previews shown on upcoming/current cards, by role.
const BRAND_SUMMARIES = {
  [STEP_KEYS.ACCEPTED]: 'Creator agreed to the terms.',
  [STEP_KEYS.GRANT_ACCESS]: 'Share how the creator accesses your digital product (links, codes, or notes).',
  [STEP_KEYS.CONFIRM_ACCESS]: 'Waiting for the creator to confirm they received access.',
  [STEP_KEYS.CONFIRM_ADDRESS]: 'Waiting for the creator to confirm their shipping address.',
  [STEP_KEYS.SHIPPING]: 'Ship the product and add tracking so the creator knows when to expect it.',
  [STEP_KEYS.CONTRACT]: 'Generate the contract and send it to the creator to sign.',
  [STEP_KEYS.CONTENT_LIVE]: 'Review and approve the creator\'s content once it\'s live.',
  [STEP_KEYS.PAID]: 'Release the agreed creator payout through Millet.',
  [STEP_KEYS.REVIEW]: '',
};

const INFLUENCER_SUMMARIES = {
  [STEP_KEYS.ACCEPTED]: 'You agreed to the terms.',
  [STEP_KEYS.GRANT_ACCESS]: 'The brand will share access details. You\'ll get a notification when it\'s ready.',
  [STEP_KEYS.CONFIRM_ACCESS]: 'Once you can access the digital product, confirm below so the collaboration can continue.',
  [STEP_KEYS.CONFIRM_ADDRESS]: 'Confirm your shipping address so the brand can send your product.',
  [STEP_KEYS.SHIPPING]: 'Confirm receipt once your product arrives, or report an issue.',
  [STEP_KEYS.CONTRACT]: 'Download, sign, and upload the contract.',
  [STEP_KEYS.CONTENT_LIVE]: 'Post, submit proof; the brand reviews before payout.',
  [STEP_KEYS.PAID]: 'Your payout is released through Millet.',
  [STEP_KEYS.REVIEW]: '',
};

export const isDigitalCampaign = (collaboration) => collaboration?.requires_shipping === false;

/** True once the brand has sent the contract (or the creator has already signed). */
export const isContractSentToCreator = (collaboration) => {
  const status = collaboration?.contract_status;
  return status === 'sent' || status === 'signed';
};
export const isLiveShopping = (collaboration) => campaignHasLivestreamDeliverables(collaboration);
export const usesCommission = (collaboration) => collaboration?.uses_commission === true;
export const isCustomContract = (collaboration) => collaboration?.contract_type === 'custom';

/**
 * Ordered list of step keys that apply to this collaboration.
 */
export const getStepSequence = (collaboration) => {
  const steps = [STEP_KEYS.ACCEPTED];
  if (isDigitalCampaign(collaboration)) {
    steps.push(STEP_KEYS.GRANT_ACCESS, STEP_KEYS.CONFIRM_ACCESS);
  } else {
    steps.push(STEP_KEYS.CONFIRM_ADDRESS, STEP_KEYS.SHIPPING);
  }
  if (isCustomContract(collaboration)) {
    steps.push(STEP_KEYS.CONTRACT);
  }
  steps.push(STEP_KEYS.CONTENT_LIVE, STEP_KEYS.PAID, STEP_KEYS.REVIEW);
  return steps;
};

export const hasCollaborationReview = (collaboration, reviewerType) => (
  Array.isArray(collaboration?.reviews)
  && collaboration.reviews.some((review) => review.reviewer_type === reviewerType)
);

const getReviewerTypeForRole = (role) => (role === 'influencer' ? 'influencer' : 'brand');

/** True when the viewing brand/influencer has already submitted their collaboration review. */
export const hasViewerLeftReview = (collaboration, role) => (
  hasCollaborationReview(collaboration, getReviewerTypeForRole(role))
);

/**
 * True when a given step is complete based on collaboration milestone fields.
 */
export const isStepComplete = (stepKey, collaboration) => {
  if (!collaboration) return false;
  switch (stepKey) {
    case STEP_KEYS.ACCEPTED:
      return true;
    case STEP_KEYS.GRANT_ACCESS:
      return !!collaboration.digital_access_granted_at;
    case STEP_KEYS.CONFIRM_ACCESS:
      return !!collaboration.digital_access_confirmed_at;
    case STEP_KEYS.CONFIRM_ADDRESS:
      return !!collaboration.address_verified;
    case STEP_KEYS.SHIPPING:
      return !!collaboration.product_received && !collaboration.reship_requested;
    case STEP_KEYS.CONTRACT:
      return !!collaboration.contract_verified_by_brand;
    case STEP_KEYS.CONTENT_LIVE:
      return collaboration.deliverable?.status === 'approved' || !!collaboration.content_posted;
    case STEP_KEYS.PAID: {
      return collaboration.flat_fee_payment_status === 'paid'
        || collaboration.payment_complete === true;
    }
    case STEP_KEYS.REVIEW:
      return collaboration.status === 'completed'
        || hasCollaborationReview(collaboration, 'brand')
        || hasCollaborationReview(collaboration, 'influencer');
    default:
      return false;
  }
};

/**
 * Derive the current step key: first applicable step not complete, else review.
 */
export const resolveCurrentStepKey = (collaboration) => {
  const sequence = getStepSequence(collaboration);
  const firstIncomplete = sequence.find((stepKey) => !isStepComplete(stepKey, collaboration));
  return firstIncomplete || STEP_KEYS.REVIEW;
};

export const isJourneyStepReached = (stepKey, collaboration) => {
  const sequence = getStepSequence(collaboration);
  const stepIdx = sequence.indexOf(stepKey);
  if (stepIdx < 0) return false;
  const currentKey = resolveCurrentStepKey(collaboration);
  const currentIdx = sequence.indexOf(currentKey);
  return currentIdx >= stepIdx || isStepComplete(stepKey, collaboration);
};

const getLabel = (stepKey, role, collaboration) => {
  if (stepKey === STEP_KEYS.CONTENT_LIVE && isLiveShopping(collaboration)) {
    return 'Go live';
  }
  const labels = role === 'influencer' ? INFLUENCER_LABELS : BRAND_LABELS;
  const fallbackLabel = labels[stepKey];
  if (stepKey === STEP_KEYS.CONTENT_LIVE) {
    return getContentLiveLabel(collaboration, role, fallbackLabel);
  }
  return fallbackLabel;
};

const getSummary = (stepKey, role, collaboration) => {
  if (stepKey === STEP_KEYS.CONTENT_LIVE) {
    return getContentLiveSummary(collaboration, role);
  }
  const summaries = role === 'influencer' ? INFLUENCER_SUMMARIES : BRAND_SUMMARIES;
  return summaries[stepKey] || '';
};

const formatJourneyDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatJourneyDateTime = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const datePart = date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
  const timePart = date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  });
  return `${datePart} at ${timePart}`;
};

const getPartnerFirstName = (collaboration, role) => {
  const value = role === 'brand'
    ? collaboration?.influencer_name
    : collaboration?.brand_name || collaboration?.company_name;
  return (value || 'They').split(' ')[0] || 'They';
};

const getBrandDisplayName = (collaboration) => (
  collaboration?.brand_name || collaboration?.company_name || 'the brand'
);

const getLivestreamScheduleAt = (collaboration) => (
  collaboration?.deliverable?.scheduled_at || collaboration?.show_scheduled_at || null
);

const isLivestreamScheduled = (collaboration) => {
  const deliverableStatus = collaboration?.deliverable?.status;
  if (getLivestreamScheduleAt(collaboration)) return true;
  return ['scheduled', 'completed', 'submitted', 'approved'].includes(deliverableStatus);
};

const isLivestreamAwaitingBrandReview = (collaboration) => {
  const deliverableStatus = collaboration?.deliverable?.status;
  if (deliverableStatus !== 'submitted' && deliverableStatus !== 'completed') return false;
  if (collaboration?.deliverable?.type === 'livestream') {
    const reviewStatus = getLivestreamReviewStatus(collaboration);
    return reviewStatus === LIVESTREAM_REVIEW_STATUS.PENDING_REVIEW
      || reviewStatus === LIVESTREAM_REVIEW_STATUS.CREATOR_RESPONDED;
  }
  return true;
};

const getContractFilename = (collaboration) => {
  if (collaboration?.contract_filename) {
    return String(collaboration.contract_filename)
      .replace(/[_-]+/g, ' ')
      .replace(/\.pdf$/i, '')
      .trim();
  }
  if (collaboration?.campaign_name) {
    return `${collaboration.campaign_name} contract`;
  }
  return 'Collaboration contract';
};

const getContractAttachment = (collaboration, { forCreator = false } = {}) => {
  if (!collaboration?.contract_url && !collaboration?.signed_contract_url) return null;
  if (forCreator && !isContractSentToCreator(collaboration)) return null;

  const sentToCreator = isContractSentToCreator(collaboration);
  const sentDate = formatJourneyDate(
    collaboration.contract_sent_at
      || (sentToCreator ? collaboration.contract_uploaded_at : null)
      || collaboration.contract_created_at,
  );
  const draftDate = formatJourneyDate(
    collaboration.contract_uploaded_at || collaboration.contract_created_at,
  );
  const signed = collaboration.contract_status === 'signed';
  const verified = !!collaboration.contract_verified_by_brand;
  let statusLabel = forCreator ? 'Needs signature' : 'Sent';
  let metaStatus = forCreator
    ? 'Waiting for your signature'
    : 'Waiting for the creator to sign';
  let status = 'pending';

  if (verified) {
    statusLabel = 'Approved';
    metaStatus = 'Ready for the next step';
    status = 'completed';
  } else if (signed) {
    statusLabel = 'Signed';
    metaStatus = 'Awaiting brand approval';
    status = 'neutral';
  } else if (collaboration.contract_revision_message) {
    statusLabel = forCreator ? 'Needs revision' : 'Changes requested';
    metaStatus = forCreator
      ? 'The brand requested changes'
      : 'Waiting for a revised signed copy';
    status = 'pending';
  } else if (!sentToCreator) {
    statusLabel = 'Draft';
    metaStatus = 'Ready to send';
    status = 'neutral';
  }

  let meta;
  if (forCreator && sentToCreator && !signed && !verified) {
    const brandName = getBrandDisplayName(collaboration);
    meta = sentDate ? `Sent ${sentDate} by ${brandName}` : `Contract sent by ${brandName}`;
  } else if (sentToCreator) {
    meta = `${sentDate ? `Sent ${sentDate}` : 'Contract sent'} · ${metaStatus}`;
  } else {
    meta = `${draftDate ? `Uploaded ${draftDate}` : 'Draft ready'} · ${metaStatus}`;
  }

  return {
    filename: getContractFilename(collaboration),
    meta,
    statusLabel,
    status,
  };
};

const PRODUCT_RECEIPT_ISSUE_LABELS = {
  damaged: 'Damaged or not functional',
  missing: 'Missing or incomplete',
  not_as_described: 'Not as described',
  not_suitable: 'Not suitable for my audience',
  other: 'Other',
};

const getProductReceiptIssueLabel = (collaboration) => {
  const issueType = collaboration?.specific_issue_type || collaboration?.specificIssueType;
  return PRODUCT_RECEIPT_ISSUE_LABELS[issueType] || null;
};

const isCollaborationCanceled = (collaboration) => {
  const status = collaboration?.acceptance_status
    || collaboration?.acceptanceStatus
    || collaboration?.status;
  return status === 'cancelled' || status === 'canceled';
};

const getShippingReceiptCompletedSummary = (collaboration, { role, partnerFirstName }) => {
  const condition = collaboration?.product_condition || collaboration?.productCondition;
  const issueLabel = getProductReceiptIssueLabel(collaboration);
  const issueSuffix = issueLabel ? ` (${issueLabel})` : '';

  if (role === 'brand') {
    if (condition === 'good') {
      return `${partnerFirstName} marked Looks good.`;
    }
    if (condition === 'not_good') {
      if (collaboration?.reship_requested || collaboration?.reshipRequested) {
        return `${partnerFirstName} reported an issue${issueSuffix} and asked for a replacement.`;
      }
      if (isCollaborationCanceled(collaboration)) {
        return `${partnerFirstName} reported an issue${issueSuffix} and ended the collaboration.`;
      }
      return `${partnerFirstName} reported an issue${issueSuffix} and is continuing the campaign.`;
    }
    return `${partnerFirstName} confirmed receipt and reviewed the product.`;
  }

  if (condition === 'good') {
    return 'You marked Looks good.';
  }
  if (condition === 'not_good') {
    if (collaboration?.reship_requested || collaboration?.reshipRequested) {
      return `You reported an issue${issueSuffix} and asked for a replacement.`;
    }
    if (isCollaborationCanceled(collaboration)) {
      return `You reported an issue${issueSuffix} and ended the collaboration.`;
    }
    return `You reported an issue${issueSuffix} and are continuing the campaign.`;
  }
  return 'You confirmed receipt and reviewed the product.';
};

export const resolveStepContext = (stepKey, collaboration, { role = 'brand' } = {}) => {
  const partnerFirstName = getPartnerFirstName(collaboration, role);
  const label = getLabel(stepKey, role, collaboration);
  const summary = getSummary(stepKey, role, collaboration);
  const deliverableStatus = collaboration?.deliverable?.status;
  const contractAttachment = getContractAttachment(collaboration);

  switch (stepKey) {
    case STEP_KEYS.ACCEPTED:
      return {
        statusTitle: role === 'brand' ? 'Creator accepted' : 'You accepted',
        statusDescription: role === 'brand'
          ? `${partnerFirstName} agreed to the collaboration terms.`
          : 'You agreed to the collaboration terms.',
        completedSummary: role === 'brand'
          ? `${partnerFirstName} agreed to the collaboration terms.`
          : 'You agreed to the collaboration terms.',
      };
    case STEP_KEYS.GRANT_ACCESS: {
      const granted = !!collaboration?.digital_access_granted_at;
      let statusTitle;
      let statusDescription;
      if (role === 'brand') {
        statusTitle = granted ? 'Access granted' : 'Grant access';
        statusDescription = granted
          ? `${partnerFirstName} can now view your access instructions.`
          : 'Share how the creator accesses your digital product, or enter N/A if no action is needed.';
      } else {
        statusTitle = granted ? 'Access shared' : 'Waiting for access';
        statusDescription = granted
          ? 'The brand shared access details. Review them before confirming.'
          : 'The brand will share access details here when ready.';
      }
      return {
        statusTitle,
        statusDescription,
        completedSummary: role === 'brand'
          ? 'You granted access to your digital product.'
          : 'The brand shared access to their digital product.',
      };
    }
    case STEP_KEYS.CONFIRM_ACCESS: {
      const confirmed = !!collaboration?.digital_access_confirmed_at;
      let statusTitle;
      let statusDescription;
      if (role === 'brand') {
        statusTitle = confirmed ? 'Access confirmed' : 'Waiting on confirmation';
        statusDescription = confirmed
          ? `${partnerFirstName} confirmed they can access your product.`
          : 'Waiting for the creator to confirm they received access.';
      } else {
        statusTitle = confirmed ? 'Access confirmed' : 'Confirm access';
        statusDescription = confirmed
          ? 'You confirmed access. The next step is ready.'
          : 'Once you can access the digital product, confirm below so the collaboration can continue.';
      }
      return {
        statusTitle,
        statusDescription,
        completedSummary: role === 'brand'
          ? `${partnerFirstName} confirmed they can access your product.`
          : 'You confirmed access to the digital product.',
      };
    }
    case STEP_KEYS.CONFIRM_ADDRESS:
      return {
        statusTitle: role === 'brand' ? 'Waiting on shipping address' : 'Confirm your shipping address',
        statusDescription: role === 'brand'
          ? `${partnerFirstName} needs to confirm their address before you can ship the product.`
          : 'Confirm your shipping address so the brand can send your product.',
        completedSummary: role === 'brand'
          ? `${partnerFirstName} confirmed their shipping address.`
          : 'Your shipping address is confirmed.',
      };
    case STEP_KEYS.SHIPPING: {
      const productShipped = !!collaboration?.product_shipped;
      const reshipRequested = !!collaboration?.reship_requested;
      const carrier = collaboration?.shipping_carrier || collaboration?.shippingCarrier;
      const trackingNumber = collaboration?.tracking_number || collaboration?.trackingNumber;
      let statusTitle;
      let statusDescription;

      if (role === 'brand') {
        if (reshipRequested) {
          statusTitle = 'Reship the product';
          statusDescription = `${partnerFirstName} asked for a replacement. Add new tracking so they can confirm the replacement arrived.`;
        } else {
          statusTitle = productShipped ? 'Waiting on product receipt' : 'Ship the product';
          statusDescription = productShipped
            ? `${partnerFirstName} needs to confirm the product arrived before moving forward.`
            : 'Ship the product and add tracking so the creator knows when to expect it.';
        }
      } else if (reshipRequested) {
        statusTitle = 'Waiting for replacement';
        statusDescription = 'The brand has your resend request. Confirm receipt once the replacement arrives.';
      } else {
        statusTitle = productShipped ? 'Product on the way' : 'Waiting on shipment';
        statusDescription = productShipped
          ? 'Track the package and confirm receipt once it arrives.'
          : 'The brand is preparing to ship your product.';
      }

      const shipmentBits = [];
      if (carrier) shipmentBits.push(carrier);
      if (trackingNumber) shipmentBits.push(trackingNumber);
      const shipmentLabel = shipmentBits.length > 0 ? shipmentBits.join(' · ') : null;

      let completedSummary = getShippingReceiptCompletedSummary(collaboration, {
        role,
        partnerFirstName,
      });
      if (shipmentLabel) {
        completedSummary = `${shipmentLabel}. ${completedSummary}`;
      }

      return {
        statusTitle,
        statusDescription,
        completedSummary,
      };
    }
    case STEP_KEYS.CONTRACT: {
      const hasContractFile = !!collaboration?.contract_url;
      const sentToCreator = isContractSentToCreator(collaboration);
      const creatorAttachment = getContractAttachment(collaboration, { forCreator: true });

      // Creators only see the contract after the brand clicks "Send to creator".
      if (role === 'influencer' && (!hasContractFile || !sentToCreator)) {
        return {
          statusTitle: 'Waiting on contract',
          statusDescription: 'The brand is preparing your contract. You will be notified when it is ready.',
          completedSummary: 'Contract completed.',
        };
      }

      if (!hasContractFile) {
        return {
          statusTitle: 'Send the contract',
          statusDescription: `Send ${partnerFirstName} the custom contract before content work begins.`,
          completedSummary: 'Contract completed.',
        };
      }

      if (role === 'brand' && !sentToCreator) {
        return {
          statusTitle: 'Send the contract',
          statusDescription: `Review the draft and send it to ${partnerFirstName} before content work begins.`,
          attachment: contractAttachment,
          completedSummary: 'Contract completed.',
        };
      }

      if (collaboration.contract_revision_message && !collaboration.contract_verified_by_brand
        && collaboration.contract_status !== 'signed') {
        return {
          statusTitle: role === 'brand' ? 'Waiting on revised contract' : 'Revise and sign contract',
          statusDescription: role === 'brand'
            ? `You requested changes. ${partnerFirstName} needs to upload a new signed copy.`
            : 'The brand requested changes. Review their note, then upload a new signed copy.',
          attachment: role === 'influencer' ? creatorAttachment : contractAttachment,
          completedSummary: 'Contract completed.',
        };
      }

      if (collaboration.contract_status === 'signed' && !collaboration.contract_verified_by_brand) {
        return {
          statusTitle: role === 'brand' ? 'Review signed contract' : 'Waiting on brand approval',
          statusDescription: role === 'brand'
            ? `${partnerFirstName} uploaded a signed contract. Review and approve it before moving to Go live.`
            : 'The brand is reviewing your signed contract. You can create content once it is approved.',
          attachment: role === 'influencer' ? creatorAttachment : contractAttachment,
          completedSummary: 'Signed contract is awaiting brand approval.',
        };
      }
      return {
        statusTitle: role === 'brand' ? 'Waiting on signed contract' : 'Sign your contract',
        statusDescription: role === 'brand'
          ? `You sent a custom contract. ${partnerFirstName} needs to review, sign, and upload it before moving to Go live.`
          : 'Review the agreement and sign it to move to the next step.',
        attachment: role === 'influencer' ? creatorAttachment : contractAttachment,
        completedSummary: role === 'brand'
          ? `${partnerFirstName} signed the contract.`
          : 'Your signed contract was approved.',
      };
    }
    case STEP_KEYS.CONTENT_LIVE: {
      const deliverableSubmitted = deliverableStatus === 'submitted' || deliverableStatus === 'completed';
      let statusTitle;
      let statusDescription;
      let scheduledAt = null;
      let scheduledDateTimeLabel = null;
      let creatorNote = null;
      let scheduleStatusLabel = null;

      if (isLiveShopping(collaboration)) {
        scheduledAt = getLivestreamScheduleAt(collaboration);
        scheduledDateTimeLabel = formatJourneyDateTime(scheduledAt);
        const isCompletedLivestream = deliverableStatus === 'completed'
          || deliverableStatus === 'approved';
        creatorNote = isCompletedLivestream
          ? null
          : (collaboration?.deliverable?.message || null);
        if (scheduledDateTimeLabel) {
          scheduleStatusLabel = 'Scheduled';
        }

        if (role === 'brand') {
          const reviewStatus = getLivestreamReviewStatus(collaboration);
          if (reviewStatus === LIVESTREAM_REVIEW_STATUS.CLARIFICATION_REQUESTED) {
            statusTitle = 'Waiting on creator response';
            statusDescription = `You asked ${partnerFirstName} for more detail. You'll review again when they respond.`;
            scheduleStatusLabel = scheduledDateTimeLabel ? 'Awaiting response' : null;
          } else if (reviewStatus === LIVESTREAM_REVIEW_STATUS.CREATOR_RESPONDED) {
            statusTitle = 'Review updated livestream';
            statusDescription = `${partnerFirstName} updated their proof. Review it to continue.`;
            scheduleStatusLabel = scheduledDateTimeLabel ? 'Awaiting approval' : null;
          } else if (isLivestreamAwaitingBrandReviewAfterResponse(collaboration)
            && deliverableStatus === 'completed') {
            statusTitle = 'Review livestream';
            statusDescription = `${partnerFirstName} submitted livestream proof. Review it to continue.`;
            scheduleStatusLabel = scheduledDateTimeLabel ? 'Awaiting approval' : null;
          } else if (isLivestreamScheduled(collaboration) && deliverableStatus !== 'completed') {
            statusTitle = 'Livestream scheduled';
            statusDescription = `${partnerFirstName} scheduled their livestream. After it airs, they'll submit proof for your review before payout.`;
          } else {
            statusTitle = 'Waiting on livestream schedule';
            statusDescription = `${partnerFirstName} needs to schedule their live stream date and time.`;
          }
        } else if (needsLivestreamClarificationResponse(collaboration)) {
          statusTitle = 'Clarification needed';
          statusDescription = 'The brand needs more information before approving your livestream.';
          scheduleStatusLabel = 'Needs your action';
        } else if (isLivestreamAwaitingBrandReview(collaboration)) {
          statusTitle = 'Waiting on brand review';
          statusDescription = 'Your live stream is complete. The brand will review it soon.';
          scheduleStatusLabel = scheduledDateTimeLabel ? 'Awaiting review' : null;
        } else if (isLivestreamScheduled(collaboration) && deliverableStatus !== 'completed') {
          statusTitle = 'Go live';
          statusDescription = 'Submit proof after you go live; the brand reviews it before payout.';
          scheduleStatusLabel = 'Needs your action';
        } else {
          statusTitle = 'Go live';
          statusDescription = 'Schedule your livestream, then submit proof. The brand reviews before payout.';
          scheduleStatusLabel = 'Needs your action';
        }
      } else {
        const context = getContentLiveStepContext({
          collaboration,
          role,
          partnerFirstName,
          deliverableSubmitted,
          fallbackTitle: label,
        });
        statusTitle = context.statusTitle;
        statusDescription = context.statusDescription;
      }

      return {
        statusTitle,
        statusDescription,
        scheduledAt,
        scheduledDateTimeLabel,
        creatorNote,
        scheduleStatusLabel,
        completedSummary: isLiveShopping(collaboration)
          ? 'The live content step is complete.'
          : 'Content was approved.',
      };
    }
    case STEP_KEYS.PAID: {
      const payoutAmounts = getCollaborationPayoutAmounts(collaboration);
      const creatorPayoutLabel = formatPaymentAmountCents(
        payoutAmounts.creatorPayoutCents,
        collaboration?.payment_currency,
      );
      const platformFeeLabel = formatPaymentAmountCents(
        payoutAmounts.platformFeeCents,
        collaboration?.payment_currency,
      );
      const brandTotalLabel = formatPaymentAmountCents(
        payoutAmounts.brandTotalCents,
        collaboration?.payment_currency,
      );
      const commissionOutstanding = hasOutstandingCampaignCommission(collaboration);
      const commissionRate = getCommissionFeeModel(
        getFinalAcceptedFeeModels(collaboration) || [],
      )?.rate;
      let completedSummary = role === 'brand'
        ? 'Creator payout has been released.'
        : 'Payment has been released to you.';
      if (role === 'brand' && creatorPayoutLabel && platformFeeLabel && brandTotalLabel) {
        completedSummary = `Brand total ${brandTotalLabel}: creator compensation ${creatorPayoutLabel} + Millet fee ${platformFeeLabel}.`;
      } else if (role === 'brand' && creatorPayoutLabel) {
        completedSummary = `Creator payout of ${creatorPayoutLabel} has been released.`;
      } else if (role === 'influencer' && creatorPayoutLabel) {
        completedSummary = `Review payment: you were paid ${creatorPayoutLabel}.`;
      }
      if (commissionOutstanding && commissionRate != null) {
        if (role === 'brand') {
          completedSummary = creatorPayoutLabel
            ? `Creator payout of ${creatorPayoutLabel} released. Commission (${commissionRate}%) still due after GMV.`
            : `Creator payout released. Commission (${commissionRate}%) still due after GMV.`;
        } else {
          completedSummary = creatorPayoutLabel
            ? `Review payment: you were paid ${creatorPayoutLabel}. Commission (${commissionRate}%) still pending after GMV.`
            : `Payment released. Commission (${commissionRate}%) still pending after GMV.`;
        }
      }
      return {
        statusTitle: role === 'brand' ? 'Pay creator' : 'Waiting on payment',
        statusDescription: role === 'brand'
          ? 'The creator has completed the deliverable. Release the agreed creator payment to complete this step.'
          : 'Your payout will be released through Millet after the brand completes payment.',
        completedSummary,
      };
    }
    case STEP_KEYS.REVIEW:
      return {
        statusTitle: 'Leave a review',
        statusDescription: '',
        completedSummary: 'Collaboration review is complete.',
      };
    default:
      return {
        statusTitle: label,
        statusDescription: summary,
        completedSummary: summary,
      };
  }
};

const chip = (label, isWaiting = false) => ({ label, isWaiting });

const getStatusLabel = (status) => STATUS_LABELS[status] || STATUS_LABELS[JOURNEY_STATUSES.NOT_STARTED];

const normalizeJourneyActor = (role) => (role === 'influencer' ? 'creator' : role);

const getOwnerLabel = (ownerRole, viewerRole) => {
  if (!ownerRole) return null;
  if (ownerRole === 'millet') return 'Millet';
  if (ownerRole === normalizeJourneyActor(viewerRole)) return 'You';
  return ownerRole === 'brand' ? 'Brand' : 'Creator';
};

function buildAction({
  stepKey,
  role,
  ownerRole = null,
  status = JOURNEY_STATUSES.NOT_STARTED,
  chipLabel,
  primaryCtaLabel = null,
  secondaryCtas = [],
  blockedReason = '',
  notificationTrigger = '',
}) {
  const viewerActor = normalizeJourneyActor(role);
  const isWaiting = Boolean(
    ownerRole
    && ownerRole !== viewerActor
    && ownerRole !== 'millet'
    && ![
      JOURNEY_STATUSES.COMPLETE,
      JOURNEY_STATUSES.PAID,
      JOURNEY_STATUSES.CANCELED,
      JOURNEY_STATUSES.NOT_STARTED,
    ].includes(status),
  );

  return {
    stepKey,
    status,
    statusLabel: getStatusLabel(status),
    ownerRole,
    ownerLabel: getOwnerLabel(ownerRole, role),
    isWaiting,
    chipLabel: chipLabel || getStatusLabel(status),
    primaryCtaLabel,
    secondaryCtas,
    blockedReason,
    notificationTrigger,
  };
}

export const isCancelledCollaboration = (collaboration) => {
  const acceptanceStatus = collaboration?.acceptanceStatus || collaboration?.acceptance_status;
  return acceptanceStatus === 'cancelled' || acceptanceStatus === 'canceled'
    || collaboration?.status === 'cancelled' || collaboration?.status === 'canceled';
};

export const isPausedCollaboration = (collaboration) => {
  const acceptanceStatus = collaboration?.acceptanceStatus || collaboration?.acceptance_status;
  return acceptanceStatus === 'paused'
    || collaboration?.acceptance_status === 'paused'
    || collaboration?.status === 'paused';
};

export const isJourneyLockedCollaboration = (collaboration) => (
  isCancelledCollaboration(collaboration) || isPausedCollaboration(collaboration)
);

const isActiveCollaboration = (collaboration) => (
  collaboration?.status === 'active'
  || collaboration?.status === 'accepted'
  || collaboration?.acceptanceStatus === 'active'
);

function resolveJourneyActionForStepKey(stepKey, collaboration, role) {
  if (!collaboration) {
    return buildAction({
      stepKey,
      role,
      status: JOURNEY_STATUSES.NOT_STARTED,
      chipLabel: 'Getting started',
    });
  }

  const ds = collaboration.deliverable?.status;
  const productShipped = collaboration.product_shipped || collaboration.productShipped;
  const reshipRequested = collaboration.reship_requested || collaboration.reshipRequested;

  switch (stepKey) {
    case STEP_KEYS.ACCEPTED:
      return buildAction({
        stepKey,
        role,
        ownerRole: role === 'brand' ? 'creator' : 'brand',
        status: role === 'brand' ? JOURNEY_STATUSES.WAITING_ON_CREATOR : JOURNEY_STATUSES.NOT_STARTED,
        chipLabel: role === 'brand' ? 'Waiting on creator' : 'Getting started',
        notificationTrigger: 'Brand accepts creator: notify both and send them to the collaboration hub.',
      });
    case STEP_KEYS.GRANT_ACCESS:
      if (!collaboration.digital_access_granted_at) {
        return buildAction({
          stepKey,
          role,
          ownerRole: 'brand',
          status: role === 'brand' ? JOURNEY_STATUSES.NEEDS_YOUR_ACTION : JOURNEY_STATUSES.WAITING_ON_BRAND,
          chipLabel: role === 'brand' ? 'Grant access' : 'Waiting on brand',
          primaryCtaLabel: role === 'brand' ? 'Grant access' : null,
          secondaryCtas: ['Message partner', 'View campaign brief'],
          notificationTrigger: 'Notify the creator when access details are shared.',
        });
      }
      return buildAction({
        stepKey,
        role,
        ownerRole: 'creator',
        status: role === 'influencer' ? JOURNEY_STATUSES.NEEDS_YOUR_ACTION : JOURNEY_STATUSES.WAITING_ON_CREATOR,
        chipLabel: role === 'influencer' ? 'Confirm access' : 'Waiting on creator',
        primaryCtaLabel: role === 'influencer' ? 'Confirm access' : null,
        secondaryCtas: ['Message partner'],
      });
    case STEP_KEYS.CONFIRM_ACCESS:
      if (!collaboration.digital_access_confirmed_at) {
        return buildAction({
          stepKey,
          role,
          ownerRole: 'creator',
          status: role === 'influencer' ? JOURNEY_STATUSES.NEEDS_YOUR_ACTION : JOURNEY_STATUSES.WAITING_ON_CREATOR,
          chipLabel: role === 'influencer' ? 'Confirm access' : 'Waiting on creator',
          primaryCtaLabel: role === 'influencer' ? 'Confirm access' : null,
          secondaryCtas: ['Message partner'],
          notificationTrigger: 'Notify the brand when the creator confirms access.',
        });
      }
      return buildAction({
        stepKey,
        role,
        ownerRole: null,
        status: JOURNEY_STATUSES.COMPLETE,
        chipLabel: 'Complete',
      });
    case STEP_KEYS.CONFIRM_ADDRESS:
      return buildAction({
        stepKey,
        role,
        ownerRole: 'creator',
        status: role === 'influencer' ? JOURNEY_STATUSES.NEEDS_YOUR_ACTION : JOURNEY_STATUSES.WAITING_ON_CREATOR,
        chipLabel: role === 'influencer' ? 'Confirm address' : 'Waiting on creator',
        primaryCtaLabel: role === 'influencer' ? 'Confirm address' : null,
        secondaryCtas: ['Message partner', 'View campaign brief'],
        notificationTrigger: 'Notify the brand when the creator confirms their shipping address.',
      });
    case STEP_KEYS.SHIPPING:
      if (reshipRequested) {
        if (role === 'brand') {
          return buildAction({
            stepKey,
            role,
            ownerRole: 'brand',
            status: JOURNEY_STATUSES.NEEDS_YOUR_ACTION,
            chipLabel: 'Reship product',
            primaryCtaLabel: 'Reship the product',
            secondaryCtas: ['Message creator', 'View address'],
            notificationTrigger: 'Notify the creator when replacement tracking is added.',
          });
        }
        return buildAction({
          stepKey,
          role,
          ownerRole: 'brand',
          status: JOURNEY_STATUSES.WAITING_ON_BRAND,
          chipLabel: 'Waiting for replacement',
          primaryCtaLabel: null,
          secondaryCtas: ['Message brand', 'View tracking'],
        });
      }
      if (role === 'brand') {
        if (!productShipped) {
          return buildAction({
            stepKey,
            role,
            ownerRole: 'brand',
            status: JOURNEY_STATUSES.NEEDS_YOUR_ACTION,
            chipLabel: 'Ship product',
            primaryCtaLabel: 'Ship product',
            secondaryCtas: ['Message creator', 'View address'],
            notificationTrigger: 'Notify the creator when tracking is added.',
          });
        }
        return buildAction({
          stepKey,
          role,
          ownerRole: 'creator',
          status: JOURNEY_STATUSES.WAITING_ON_CREATOR,
          chipLabel: 'Waiting on creator',
          primaryCtaLabel: null,
          secondaryCtas: ['View tracking', 'Message creator'],
        });
      }
      if (!productShipped) {
        return buildAction({
          stepKey,
          role,
          ownerRole: 'brand',
          status: JOURNEY_STATUSES.WAITING_ON_BRAND,
          chipLabel: 'Waiting on brand',
          secondaryCtas: ['Message brand'],
        });
      }
      if (!collaboration.product_received) {
        return buildAction({
          stepKey,
          role,
          ownerRole: 'creator',
          status: JOURNEY_STATUSES.NEEDS_YOUR_ACTION,
          chipLabel: 'Confirm receipt',
          primaryCtaLabel: 'Confirm product received',
          secondaryCtas: ['View tracking', 'Report product issue'],
          notificationTrigger: 'Notify the brand when the creator confirms receipt or reports an issue.',
        });
      }
      return buildAction({
        stepKey,
        role,
        ownerRole: null,
        status: JOURNEY_STATUSES.COMPLETE,
        chipLabel: 'Complete',
      });
    case STEP_KEYS.CONTRACT:
      if (!collaboration.contract_url || !isContractSentToCreator(collaboration)) {
        if (role === 'influencer') {
          return buildAction({
            stepKey,
            role,
            ownerRole: 'brand',
            status: JOURNEY_STATUSES.WAITING_ON_BRAND,
            chipLabel: 'Waiting on brand',
            primaryCtaLabel: null,
            secondaryCtas: ['Message partner'],
            notificationTrigger: 'Notify the creator when the contract is sent.',
          });
        }
        return buildAction({
          stepKey,
          role,
          ownerRole: 'brand',
          status: JOURNEY_STATUSES.NEEDS_YOUR_ACTION,
          chipLabel: 'Send contract',
          primaryCtaLabel: collaboration.contract_url ? 'Send to creator' : 'Upload contract',
          secondaryCtas: ['Message partner'],
          notificationTrigger: 'Notify the creator when the contract is sent.',
        });
      }
      if (collaboration.contract_status !== 'signed') {
        return buildAction({
          stepKey,
          role,
          ownerRole: 'creator',
          status: role === 'influencer' ? JOURNEY_STATUSES.NEEDS_YOUR_ACTION : JOURNEY_STATUSES.WAITING_ON_CREATOR,
          chipLabel: role === 'influencer' ? 'Sign contract' : 'Waiting on creator',
          primaryCtaLabel: role === 'influencer' ? 'Review and sign contract' : null,
          secondaryCtas: ['View contract', 'Message partner'],
          notificationTrigger: 'Notify the brand when the signed contract is uploaded.',
        });
      }
      if (!collaboration.contract_verified_by_brand) {
        return buildAction({
          stepKey,
          role,
          ownerRole: 'brand',
          status: role === 'brand' ? JOURNEY_STATUSES.NEEDS_YOUR_ACTION : JOURNEY_STATUSES.WAITING_ON_BRAND,
          chipLabel: role === 'brand' ? 'Review contract' : 'Waiting on brand',
          primaryCtaLabel: role === 'brand' ? 'Review signed contract' : null,
          secondaryCtas: ['View contract', 'Message partner'],
        });
      }
      return buildAction({
        stepKey,
        role,
        ownerRole: null,
        status: JOURNEY_STATUSES.COMPLETE,
        chipLabel: 'Complete',
      });
    case STEP_KEYS.CONTENT_LIVE:
      if (isLiveShopping(collaboration)) {
        const reviewStatus = getLivestreamReviewStatus(collaboration);
        if (reviewStatus === LIVESTREAM_REVIEW_STATUS.CLARIFICATION_REQUESTED) {
          return buildAction({
            stepKey,
            role,
            ownerRole: 'creator',
            status: role === 'influencer' ? JOURNEY_STATUSES.CHANGES_REQUESTED : JOURNEY_STATUSES.WAITING_ON_CREATOR,
            chipLabel: role === 'influencer' ? 'Respond to feedback' : 'Waiting on creator',
            primaryCtaLabel: role === 'influencer' ? 'Respond to feedback' : null,
            secondaryCtas: ['Message partner'],
            notificationTrigger: 'Notify the creator when the brand requests clarification or changes.',
          });
        }
        if (reviewStatus === LIVESTREAM_REVIEW_STATUS.CREATOR_RESPONDED) {
          return buildAction({
            stepKey,
            role,
            ownerRole: 'brand',
            status: role === 'brand' ? JOURNEY_STATUSES.NEEDS_YOUR_ACTION : JOURNEY_STATUSES.IN_REVIEW,
            chipLabel: role === 'brand' ? 'Review livestream' : 'In review',
            primaryCtaLabel: role === 'brand' ? 'Review content' : null,
            secondaryCtas: ['Message partner'],
            notificationTrigger: 'Notify the brand when revised livestream details are submitted.',
          });
        }
        if (ds === 'completed' || ds === 'submitted') {
          return buildAction({
            stepKey,
            role,
            ownerRole: 'brand',
            status: role === 'brand' ? JOURNEY_STATUSES.NEEDS_YOUR_ACTION : JOURNEY_STATUSES.IN_REVIEW,
            chipLabel: role === 'brand' ? 'Review livestream' : 'In review',
            primaryCtaLabel: role === 'brand' ? 'Review content' : null,
            secondaryCtas: ['Message partner'],
            notificationTrigger: 'Notify the brand when the creator marks the stream complete.',
          });
        }
      }
      if (ds === 'submitted' || ds === 'completed') {
        if (role === 'brand') {
          const { isStory } = getContentLiveDeliverableContext(collaboration);
          return buildAction({
            stepKey,
            role,
            ownerRole: 'brand',
            status: JOURNEY_STATUSES.NEEDS_YOUR_ACTION,
            chipLabel: isStory ? 'Review story' : 'Review content',
            primaryCtaLabel: 'Review content',
            secondaryCtas: ['Request changes', 'Approve content', 'Message creator'],
            notificationTrigger: 'Notify the creator when content is approved or changes are requested.',
          });
        }
        return buildAction({
          stepKey,
          role,
          ownerRole: 'brand',
          status: JOURNEY_STATUSES.IN_REVIEW,
          chipLabel: 'In review',
          secondaryCtas: ['Message brand'],
        });
      }
      if (ds === 'approved') {
        return buildAction({
          stepKey,
          role,
          ownerRole: null,
          status: JOURNEY_STATUSES.COMPLETE,
          chipLabel: 'Complete',
          notificationTrigger: 'Notify the creator that payment is now unlocked.',
        });
      }
      if (role === 'brand') {
        if (!collaborationHasPostContent(collaboration) && (!ds || ds === 'pending' || ds === 'scheduled' || ds === 'rejected')) {
          return buildAction({
            stepKey,
            role,
            ownerRole: 'creator',
            status: ds === 'rejected' ? JOURNEY_STATUSES.CHANGES_REQUESTED : JOURNEY_STATUSES.WAITING_ON_CREATOR,
            chipLabel: 'Waiting on creator',
            primaryCtaLabel: null,
            secondaryCtas: ['Message creator', 'View campaign brief'],
          });
        }
        const { isStory } = getContentLiveDeliverableContext(collaboration);
        return buildAction({
          stepKey,
          role,
          ownerRole: 'brand',
          status: JOURNEY_STATUSES.NEEDS_YOUR_ACTION,
          chipLabel: isStory ? 'Review story' : 'Review content',
          primaryCtaLabel: 'Review content',
          secondaryCtas: ['Request changes', 'Approve content'],
        });
      }
      if (ds === 'rejected') {
        return buildAction({
          stepKey,
          role,
          ownerRole: 'creator',
          status: JOURNEY_STATUSES.CHANGES_REQUESTED,
          chipLabel: 'Changes requested',
          primaryCtaLabel: 'Resubmit content',
          secondaryCtas: ['View feedback', 'Message brand'],
        });
      }
      if (ds === 'scheduled') {
        return buildAction({
          stepKey,
          role,
          ownerRole: 'creator',
          status: JOURNEY_STATUSES.SCHEDULED,
          chipLabel: 'Scheduled',
          primaryCtaLabel: 'Mark live complete',
          secondaryCtas: ['Reschedule', 'Message brand'],
        });
      }
      if (isLiveShopping(collaboration)) {
        return buildAction({
          stepKey,
          role,
          ownerRole: 'creator',
          status: JOURNEY_STATUSES.NEEDS_YOUR_ACTION,
          chipLabel: 'Schedule live',
          primaryCtaLabel: 'Schedule live',
          secondaryCtas: ['Message brand', 'View campaign brief'],
        });
      }
      return buildAction({
        stepKey,
        role,
        ownerRole: 'creator',
        status: JOURNEY_STATUSES.NEEDS_YOUR_ACTION,
        chipLabel: getContentLiveChipLabel(collaboration, role),
        primaryCtaLabel: getContentLiveChipLabel(collaboration, role) === 'Add story link'
          ? 'Submit story link'
          : 'Submit content link',
        secondaryCtas: ['Message brand', 'View campaign brief'],
        notificationTrigger: 'Notify the brand when content is submitted or revised.',
      });
    case STEP_KEYS.PAID:
      if (isPaymentStepComplete(collaboration)) {
        return buildAction({
          stepKey,
          role,
          ownerRole: null,
          status: JOURNEY_STATUSES.PAID,
          chipLabel: 'Paid',
          notificationTrigger: 'Notify the creator when payment is completed.',
        });
      }
      if (role === 'brand') {
        const creatorPayoutReady = collaboration?.stripe_connect_account_id
          && collaboration?.stripe_connect_charges_enabled
          && collaboration?.stripe_connect_payouts_enabled;
        return buildAction({
          stepKey,
          role,
          ownerRole: creatorPayoutReady ? 'brand' : 'creator',
          status: creatorPayoutReady ? JOURNEY_STATUSES.NEEDS_YOUR_ACTION : JOURNEY_STATUSES.BLOCKED,
          chipLabel: creatorPayoutReady ? 'Pay creator' : 'Blocked',
          primaryCtaLabel: creatorPayoutReady ? 'Pay creator' : null,
          secondaryCtas: ['View payment details', 'Message creator'],
          blockedReason: creatorPayoutReady
            ? ''
            : 'The creator needs to finish payout setup before you can release payment.',
        });
      }
      return buildAction({
        stepKey,
        role,
        ownerRole: collaboration?.stripe_connect_account_id && collaboration?.stripe_connect_charges_enabled && collaboration?.stripe_connect_payouts_enabled
          ? 'brand'
          : 'creator',
        status: collaboration?.stripe_connect_account_id && collaboration?.stripe_connect_charges_enabled && collaboration?.stripe_connect_payouts_enabled
          ? JOURNEY_STATUSES.PAYMENT_PENDING
          : JOURNEY_STATUSES.NEEDS_YOUR_ACTION,
        chipLabel: collaboration?.stripe_connect_account_id && collaboration?.stripe_connect_charges_enabled && collaboration?.stripe_connect_payouts_enabled
          ? 'Payment pending'
          : 'Set up payouts',
        primaryCtaLabel: collaboration?.stripe_connect_account_id && collaboration?.stripe_connect_charges_enabled && collaboration?.stripe_connect_payouts_enabled
          ? null
          : 'Set up payouts',
        secondaryCtas: ['View payment details', 'Message brand'],
      });
    case STEP_KEYS.REVIEW:
      // Each party owns their own review — the other party's submission must not clear this CTA.
      if (hasViewerLeftReview(collaboration, role)) {
        return buildAction({
          stepKey,
          role,
          ownerRole: null,
          status: JOURNEY_STATUSES.COMPLETE,
          chipLabel: 'Complete',
        });
      }
      return buildAction({
        stepKey,
        role,
        ownerRole: normalizeJourneyActor(role),
        status: JOURNEY_STATUSES.NEEDS_YOUR_ACTION,
        chipLabel: 'Leave review',
        primaryCtaLabel: 'Leave review',
        secondaryCtas: ['Message partner'],
        notificationTrigger: 'Notify both users when the collaboration is completed.',
      });
    default:
      return buildAction({
        stepKey,
        role,
        status: JOURNEY_STATUSES.NOT_STARTED,
        chipLabel: 'Not started',
      });
  }
}

/**
 * Build the role-specific journey definition for the UI.
 * @param {Object} collaboration
 * @param {Object} [options]
 * @param {'brand'|'influencer'} [options.role='brand']
 * @returns {{ role, currentStepKey, totalSteps, completedCount, steps: Array }}
 */
export const buildJourneyDefinition = (collaboration, { role = 'brand' } = {}) => {
  const sequence = getStepSequence(collaboration);
  const currentKey = resolveCurrentStepKey(collaboration);
  const completedMap = collaboration?.step_completed_at || {};

  const steps = sequence.map((key) => {
    const complete = isStepComplete(key, collaboration);
    let status = 'upcoming';
    if (complete) status = 'completed';
    else if (key === currentKey) status = 'current';
    const context = resolveStepContext(key, collaboration, { role });
    let action;
    if (complete) {
      action = buildAction({
        stepKey: key,
        role,
        ownerRole: null,
        status: JOURNEY_STATUSES.COMPLETE,
        chipLabel: 'Complete',
      });
    } else if (key === currentKey) {
      action = resolveJourneyActionForStepKey(key, collaboration, role);
    } else {
      action = buildAction({
        stepKey: key,
        role,
        ownerRole: null,
        status: JOURNEY_STATUSES.NOT_STARTED,
        chipLabel: 'Not started',
      });
    }
    return {
      key,
      label: getLabel(key, role, collaboration),
      summary: getSummary(key, role, collaboration),
      badge: STEP_BADGES[key] || null,
      status,
      action,
      displayStatus: action.status,
      displayStatusLabel: action.statusLabel,
      ownerRole: action.ownerRole,
      ownerLabel: action.ownerLabel,
      primaryCtaLabel: action.primaryCtaLabel,
      blockedReason: action.blockedReason,
      secondaryCtas: action.secondaryCtas,
      completedAt: completedMap[key] || null,
      ...context,
    };
  });

  return {
    role,
    currentStepKey: currentKey,
    totalSteps: steps.length,
    completedCount: steps.filter((s) => s.status === 'completed').length,
    steps,
  };
};

const getChipForStepKey = (stepKey, collaboration, role) => {
  const action = resolveJourneyActionForStepKey(stepKey, collaboration, role);
  return {
    label: action.chipLabel,
    isWaiting: action.isWaiting,
    status: action.status,
    statusLabel: action.statusLabel,
    ownerRole: action.ownerRole,
    ownerLabel: action.ownerLabel,
    primaryCtaLabel: action.primaryCtaLabel,
    blockedReason: action.blockedReason,
  };
};

/**
 * Short label for list chips / KPI widgets (brand or influencer).
 * Uses action-state resolution for the current step only (avoids buildJourneyDefinition).
 * @returns {{ label: string, isWaiting: boolean }}
 */
export const getJourneyChipLabel = (collaboration, role = 'brand') => {
  if (!collaboration) return chip('Getting started', false);
  if (isCancelledCollaboration(collaboration)) {
    return {
      ...chip('Canceled', false),
      status: JOURNEY_STATUSES.CANCELED,
      statusLabel: getStatusLabel(JOURNEY_STATUSES.CANCELED),
    };
  }
  if (collaboration.status === 'completed') {
    // Completed acceptances are no longer "active", but a party may still owe a review.
    if (hasViewerLeftReview(collaboration, role)) {
      return {
        ...chip('Complete', false),
        status: JOURNEY_STATUSES.COMPLETE,
        statusLabel: getStatusLabel(JOURNEY_STATUSES.COMPLETE),
      };
    }
    return getChipForStepKey(STEP_KEYS.REVIEW, collaboration, role);
  }

  if (!isActiveCollaboration(collaboration)) {
    const waiting = role === 'brand';
    return {
      ...chip(waiting ? 'Waiting on creator' : 'Getting started', waiting),
      status: waiting ? JOURNEY_STATUSES.WAITING_ON_CREATOR : JOURNEY_STATUSES.NOT_STARTED,
      statusLabel: getStatusLabel(waiting ? JOURNEY_STATUSES.WAITING_ON_CREATOR : JOURNEY_STATUSES.NOT_STARTED),
      ownerRole: waiting ? 'creator' : 'brand',
      ownerLabel: getOwnerLabel(waiting ? 'creator' : 'brand', role),
    };
  }

  const currentStepKey = resolveCurrentStepKey(collaboration);
  return getChipForStepKey(currentStepKey, collaboration, role);
};

export const getJourneyActionState = (collaboration, role = 'brand') => {
  if (!collaboration) {
    return buildAction({
      stepKey: STEP_KEYS.ACCEPTED,
      role,
      status: JOURNEY_STATUSES.NOT_STARTED,
      chipLabel: 'Getting started',
    });
  }
  if (isCancelledCollaboration(collaboration)) {
    return buildAction({
      stepKey: resolveCurrentStepKey(collaboration),
      role,
      status: JOURNEY_STATUSES.CANCELED,
      chipLabel: 'Canceled',
      blockedReason: 'This collaboration was canceled. The timeline remains available for reference.',
    });
  }
  if (collaboration.status === 'completed') {
    if (hasViewerLeftReview(collaboration, role)) {
      return buildAction({
        stepKey: STEP_KEYS.REVIEW,
        role,
        status: JOURNEY_STATUSES.COMPLETE,
        chipLabel: 'Complete',
      });
    }
    return resolveJourneyActionForStepKey(STEP_KEYS.REVIEW, collaboration, role);
  }

  const currentStepKey = resolveCurrentStepKey(collaboration);
  return resolveJourneyActionForStepKey(currentStepKey, collaboration, role);
};

/**
 * Progress stats for KPI widgets and next-best-action cards.
 * Uses step sequence + completion checks only (avoids buildJourneyDefinition).
 */
export const getJourneyProgress = (collaboration) => {
  const sequence = getStepSequence(collaboration);
  const currentStepKey = resolveCurrentStepKey(collaboration);
  const currentIndex = sequence.findIndex((key) => key === currentStepKey);
  const idx = currentIndex >= 0 ? currentIndex : Math.max(sequence.length - 1, 0);
  const completedCount = sequence.filter((key) => isStepComplete(key, collaboration)).length;
  return {
    currentIndex: idx,
    currentStep: idx + 1,
    totalSteps: sequence.length,
    completedCount,
    currentStepKey,
  };
};

/**
 * Waiting state for detail-page journey banners.
 * Uses step summaries (not buildJourneyDefinition) so Overview cold path stays lean.
 */
export const getJourneyWaitingState = (collaboration, role = 'brand') => {
  const chipMeta = getJourneyChipLabel(collaboration, role);

  if (!chipMeta.isWaiting) {
    return { isWaiting: false, waitingOn: null, message: '' };
  }

  let waitingOn = role === 'brand' ? 'creator' : 'brand';
  if (chipMeta.label === 'Waiting for confirmation') waitingOn = 'creator';
  if (chipMeta.label === 'Waiting for access') waitingOn = 'brand';

  const stepKey = resolveCurrentStepKey(collaboration);
  const summaries = role === 'influencer' ? INFLUENCER_SUMMARIES : BRAND_SUMMARIES;

  return {
    isWaiting: true,
    waitingOn,
    message: summaries[stepKey] || chipMeta.statusLabel || '',
  };
};

/** Ordered display labels for the applicable journey steps. */
export const getJourneyStepLabels = (collaboration, role = 'brand') => (
  buildJourneyDefinition(collaboration, { role }).steps.map((s) => s.label)
);
